import React, { createContext, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

import useQueryParams from 'hooks/useQueryParams';
import useDebouncedValue from 'hooks/useDebouncedValue';

import { SERVICE_GROUPS_SORT_COLUMNS } from '../constants';
import useSentinelTeamsQuery from '../hooks/useSentinelTeamsQuery';
import useApiEnvironmentQuery from '../hooks/useApiEnvironmentQuery';

export const TeamsContext = createContext({});

// BILLING_CONTACT and MEMBERS are display-only: they have no `sortBy` in
// useTeamsColumns and are missing from the API's SentinelTeamsSortColumns enum.
const UNSORTABLE_COLUMNS = [
  SERVICE_GROUPS_SORT_COLUMNS.BILLING_CONTACT,
  SERVICE_GROUPS_SORT_COLUMNS.MEMBERS,
];

const SORT_COLUMNS = Object.values(SERVICE_GROUPS_SORT_COLUMNS).filter(
  (column) => !UNSORTABLE_COLUMNS.includes(column),
);

const SORTING_TYPE_BY_DIRECTION = {
  ASC: 'ascending',
  DESC: 'descending',
};

const SORT_DIRECTIONS = Object.keys(SORTING_TYPE_BY_DIRECTION);

const DIRECTION_BY_SORTING_TYPE = {
  ascending: 'ASC',
  descending: 'DESC',
};

// Sort lives in the URL (next to the page) so a reload or a shared link keeps
// the order. `sort` and `sortDirection` are flat query params, read as strings.
const DEFAULT_TABLE_PARAMS = {
  page: 1,
  sort: undefined,
  sortDirection: undefined,
};

export default function TeamsProvider({ children }) {
  const [tableParams, setTableParams] = useQueryParams(
    'all-teams-pagination',
    DEFAULT_TABLE_PARAMS,
  );
  const { page, sort: sortColumn, sortDirection } = tableParams;

  // A hand-edited URL can hold any value; anything outside the known columns
  // and directions is treated as unsorted instead of being sent to the API.
  // `includes` compares strictly, so an array (`sortDirection[]=DESC`) or an
  // inherited key (`toString`) is rejected, unlike a property lookup.
  const hasValidSort =
    SORT_COLUMNS.includes(sortColumn) &&
    SORT_DIRECTIONS.includes(sortDirection);

  // `null` when unsorted: the API and the Apollo mocks expect `sort: null`.
  const sort = useMemo(
    () =>
      hasValidSort ? { column: sortColumn, direction: sortDirection } : null,
    [hasValidSort, sortColumn, sortDirection],
  );

  const defaultSort = useMemo(
    () =>
      hasValidSort
        ? {
            sortingColumn: sortColumn,
            sortingType: SORTING_TYPE_BY_DIRECTION[sortDirection],
          }
        : {},
    [hasValidSort, sortColumn, sortDirection],
  );

  const onSort = useCallback(
    (column, sortingType) => {
      const direction = DIRECTION_BY_SORTING_TYPE[sortingType];

      setTableParams((params) => ({
        ...params,
        page: 1,
        sort: direction ? column : undefined,
        sortDirection: direction,
      }));
    },
    [setTableParams],
  );

  const [searchText, setSearchTextValue, debouncedSearchText] =
    useDebouncedValue('');

  const { data: { version: { environment } = {} } = {} } =
    useApiEnvironmentQuery({});

  const {
    data: {
      sentinelTeams: {
        collection: allSentinelTeams = [],
        pagination = {},
      } = {},
    } = {},
    loading,
  } = useSentinelTeamsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      page,
      sort,
      search: debouncedSearchText || null,
    },
  });

  /* istanbul ignore next -- @preserve */
  const setPage = useCallback(
    (nextPage) => {
      setTableParams((params) => ({ ...params, page: nextPage }));
    },
    [setTableParams],
  );

  const resetPage = useCallback(() => {
    setTableParams((params) => ({ ...params, page: 1 }));
  }, [setTableParams]);

  const setSearchText = useCallback(
    (nextSearchText) => {
      setSearchTextValue(nextSearchText);
      resetPage();
    },
    [resetPage, setSearchTextValue],
  );

  const values = useMemo(
    () => ({
      environment,
      allSentinelTeams,
      loading,
      pagination,
      setPage,
      resetPage,
      defaultSort,
      onSort,
      searchText,
      setSearchText,
    }),
    [
      environment,
      allSentinelTeams,
      loading,
      pagination,
      setPage,
      resetPage,
      defaultSort,
      onSort,
      searchText,
      setSearchText,
    ],
  );

  return (
    <TeamsContext.Provider value={values}>{children}</TeamsContext.Provider>
  );
}

export function useTeams() {
  return useContext(TeamsContext);
}

TeamsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
